import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type Params, useMatches, useSearchParams } from 'react-router';

import type { PageHeading } from '@/components/layout/AppShell';
import { can } from '@/config/access';
import { CAMPAIGNS_PATH } from '@/config/paths';
import type { AppRoute } from '@/config/routes';
import { useActiveMembership, useSession } from '@/features/auth/useSession';
import { useBrands } from '@/features/brandHub/brand/useBrands';
import { useTemplates } from '@/features/brandHub/templates/useTemplates';
import { useCampaign, useCampaignPage } from '@/features/campaigns/campaign/useCampaigns';

import { isRouteHandle, LIVE_HEADING } from './layout';

/** Returns the route table row and the params of the deepest matched route. */
export function useMatchedRoute(): { route: AppRoute | null; params: Readonly<Params> } {
  const match = useMatches().at(-1);
  return {
    route: match && isRouteHandle(match.handle) ? match.handle.route : null,
    params: match?.params ?? {},
  };
}

/** Returns the title, subtitle and back link of the matched route. */
export function usePageHeading(): PageHeading {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [today] = useState(() => new Date());
  const { route, params } = useMatchedRoute();
  const { campaign } = useCampaign(params.campaignId);
  // Opens the campaign listener on the list page only.
  const { data: campaigns } = useCampaignPage(route?.path === CAMPAIGNS_PATH);
  const { data: brands } = useBrands();
  const { data: templates } = useTemplates();
  const session = useSession();
  const membership = useActiveMembership();

  if (!route || !can(membership, route.action)) return { title: t('notFound.title') };

  const heading: PageHeading = {
    title: t(route.titleKey),
    subtitle: route.subtitleKey && t(route.subtitleKey),
    crumb: route.crumb && { label: t(route.crumb.labelKey), to: route.crumb.to },
  };
  if (!route.liveHeading) return heading;
  const context = { t, language: i18n.language, today, params, searchParams, campaign, campaigns, brands, templates, session, membership };
  return { ...heading, ...LIVE_HEADING[route.liveHeading](context) };
}
